import { useEffect } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import AuthCallback from "@/pages/AuthCallback";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import AcceptInvite from "@/pages/AcceptInvite";
import Extension from "@/pages/Extension";
import DesktopAgent from "@/pages/DesktopAgent";
import Legal from "@/pages/Legal";
import Toaster from "@/components/ui/Toaster";
import NotFound from "@/pages/NotFound";
import Notifications from "@/pages/Notifications";
import WhatsNew from "@/pages/WhatsNew";
import Referral from "@/pages/Referral";
// Mounted once per role shell below — see the note on each route. A single flat
// route would render outside every DashboardLayout (losing the sidebar) and
// outside every RequireAuth gate with it.
import OurProducts from "@/pages/OurProducts";

import RequireAuth from "@/components/auth/RequireAuth";
import TwoFactorSetup from "@/pages/security/TwoFactorSetup";
import NoAccess from "@/pages/NoAccess";
import MyActivity from "@/pages/MyActivity";
import PmProjects from "@/pages/pm/PmProjects";
import PmProjectDetail from "@/pages/pm/PmProjectDetail";
import PmBatchReport from "@/pages/pm/PmBatchReport";
import PmTeam from "@/pages/pm/PmTeam";
import PmActivity from "@/pages/pm/PmActivity";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import TesterHome from "@/pages/dashboard/TesterHome";
import GenerateTests from "@/pages/dashboard/GenerateTests";
import TesterRunsBugs from "@/pages/dashboard/RunsBugs";
import ProjectsDisabled from "@/pages/dashboard/ProjectsDisabled";
import Notes from "@/pages/dashboard/Notes";
import ExtensionRunner from "@/pages/dashboard/ExtensionRunner";
import ExeRunner from "@/pages/dashboard/ExeRunner";
import TesterSettings from "@/pages/dashboard/Settings";
import TesterBilling from "@/pages/dashboard/Billing";

import AdminOverview from "@/pages/admin/AdminOverview";
import Analytics from "@/pages/admin/Analytics";
import PlatformUsage from "@/pages/admin/PlatformUsage";
import ModelConfig from "@/pages/admin/ModelConfig";
import ApiKeys from "@/pages/admin/ApiKeys";
import Guardrails from "@/pages/admin/Guardrails";
import Firewall from "@/pages/admin/Firewall";
import System from "@/pages/admin/System";
import Subscriptions from "@/pages/admin/Subscriptions";
import Billing from "@/pages/admin/Billing";
import Plans from "@/pages/admin/Plans";
import AuditLogPage from "@/pages/admin/AuditLog";
import Companies from "@/pages/admin/Companies";
import CompanyDetail from "@/pages/admin/CompanyDetail";
import Coupons from "@/pages/admin/Coupons";
import QaContext from "@/pages/admin/QaContext";
import Changelog from "@/pages/admin/Changelog";
import PlatformOps from "@/pages/admin/PlatformOps";
import CountryPricing from "@/pages/admin/CountryPricing";
import Support from "@/pages/admin/Support";
import AdminSettings from "@/pages/admin/AdminSettings";

// Developer dashboard — review reports for assigned projects, mark fixes.
import DevProjects from "@/pages/developer/DevProjects";
import DevProjectDetail from "@/pages/developer/DevProjectDetail";
import DevBatchReport from "@/pages/developer/DevBatchReport";
import DevBuildTools from "@/pages/developer/DevBuildTools";

// Admin (company owner) dashboard
import CompanyOverview from "@/pages/company/Overview";
import CompanyUsers from "@/pages/company/Users";
import RunsBugs from "@/pages/company/RunsBugs";
import CompanyAnalytics from "@/pages/company/Analytics";
import CompanyBilling from "@/pages/company/Billing";
import CompanyActivityLog from "@/pages/company/ActivityLog";
// WITHDRAWN from the company surface — see the note on the routes below. The
// page itself is still in the tree; uncomment to bring it back.
// import CompanyIntegrations from "@/pages/company/Integrations";
import CompanyUsageReport from "@/pages/company/UsageReport";
import CompanySettings from "@/pages/company/Settings";
// import CompanyApiKeys from "@/pages/company/ApiKeys";   // WITHDRAWN — see below

// Reset scroll position on every route change — unless the target carries a
// hash, in which case scroll to that section instead. The footer renders on the
// legal pages too and links back to "/#pricing" and friends; without this those
// links landed at the top of the home page rather than at the section.
function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (!hash) {
      window.scrollTo(0, 0);
      return undefined;
    }
    // One frame later: the destination route has to mount before its section
    // exists to scroll to.
    const raf = requestAnimationFrame(() => {
      const el = document.getElementById(hash.slice(1));
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      else window.scrollTo(0, 0);
    });
    return () => cancelAnimationFrame(raf);
  }, [pathname, hash]);
  return null;
}

export default function App() {
  return (
    <>
      <ScrollToTop />
      <Toaster />
      <Routes>
        {/* public */}
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/accept-invite" element={<AcceptInvite />} />
        <Route path="/extension" element={<Extension />} />
        <Route path="/desktop-agent" element={<DesktopAgent />} />
        <Route path="/no-access" element={<NoAccess />} />
        <Route path="/privacy" element={<Legal kind="privacy" />} />
        <Route path="/terms" element={<Legal kind="terms" />} />

        {/* Two-factor enrolment — a direct link for any signed-in user, whatever
            their role. The same card also lives on each role's
            Settings → Security tab; this standalone route is what the SuperAdmin
            nav points at, since every other link in that nav sits under /admin. */}
        <Route element={<RequireAuth />}>
          <Route path="/security/2fa" element={<TwoFactorSetup />} />
        </Route>

        {/* protected app */}
        {/* Each dashboard tree declares which roles may load it. Without this a
            signed-in tester could render the developer/admin shell and only
            discover it's forbidden when every request inside 403s. */}
        <Route element={<RequireAuth allow={["tester", "admin", "superadmin"]} />}>
          {/* Tester dashboard */}
          <Route path="/dashboard" element={<DashboardLayout role="tester" />}>
            <Route index element={<TesterHome />} />
            <Route path="generate" element={<GenerateTests />} />
            {/* Runs & Bugs — one combined page (runs + bugs sub-tabs), same
                style as the admin Runs & Bugs page. The legacy /bugs path
                REDIRECTS rather than rendering a second copy: it used to mount
                the page with an `initialView="bugs"` prop the component never
                accepted, so the link opened the Extension runs tab — and, being
                a path no nav entry matches, it also left the sidebar unhighlighted
                and the breadcrumb reading "Dashboard". Bugs are a toggle inside
                each section of the page, not a route. */}
            <Route path="runs" element={<TesterRunsBugs />} />
            <Route path="bugs" element={<Navigate to="/dashboard/runs" replace />} />
            <Route path="notes" element={<Notes />} />
            <Route path="extension" element={<ExtensionRunner />} />
            <Route path="exe" element={<ExeRunner />} />
            <Route path="settings" element={<TesterSettings />} />
            <Route path="billing" element={<TesterBilling />} />
            <Route path="referral" element={<Referral />} />
            <Route path="whats-new" element={<WhatsNew />} />
            <Route path="notifications" element={<Notifications />} />
            <Route path="activity-log" element={<MyActivity />} />
            {/* Inside this shell, so it keeps the tester sidebar + topbar. */}
            <Route path="our-other-products" element={<OurProducts />} />
          </Route>

          {/* Projects (Tester surface) — cloud/headless dashboard testing is
              paused. The tab is disabled in the sidebar and the routes are
              hard-blocked with a "temporarily disabled" notice. The company
              admin Projects surface (/admin-co/projects) is unaffected. */}
          <Route element={<DashboardLayout role="tester" />}>
            <Route path="/projects" element={<ProjectsDisabled />} />
            <Route path="/projects/:id" element={<ProjectsDisabled />} />
          </Route>
        </Route>

        {/* Project Manager dashboard — plans the work and watches all of it.
            Admins are allowed in so a company without a PM is never locked out
            of its own projects. */}
        <Route element={<RequireAuth allow={["project_manager", "admin", "superadmin"]} />}>
          <Route path="/pm" element={<DashboardLayout role="project_manager" />}>
            <Route index element={<PmProjects />} />
            <Route path="projects/:id" element={<PmProjectDetail />} />
            <Route path="projects/:id/:kind/:batchId" element={<PmBatchReport />} />
            <Route path="runs" element={<TesterRunsBugs />} />
            <Route path="team" element={<PmTeam />} />
            <Route path="activity-log" element={<PmActivity />} />
            <Route path="settings" element={<TesterSettings />} />
            <Route path="notifications" element={<Notifications />} />
            <Route path="whats-new" element={<WhatsNew />} />
            {/* Inside this shell, so it keeps the PM sidebar + topbar. */}
            <Route path="our-other-products" element={<OurProducts />} />
          </Route>
        </Route>

        {/* Developer dashboard — read-only review surface for assigned projects. */}
        <Route element={<RequireAuth allow={["developer", "admin", "superadmin"]} />}>
          <Route path="/developer" element={<DashboardLayout role="developer" />}>
            <Route index element={<DevProjects />} />
            <Route path="projects/:id" element={<DevProjectDetail />} />
            <Route path="projects/:id/:kind/:batchId" element={<DevBatchReport />} />
            <Route path="build" element={<DevBuildTools />} />
            <Route path="activity-log" element={<MyActivity />} />
            <Route path="settings" element={<TesterSettings />} />
            <Route path="whats-new" element={<WhatsNew />} />
            <Route path="notifications" element={<Notifications />} />
            {/* Inside this shell, so it keeps the developer sidebar + topbar. */}
            <Route path="our-other-products" element={<OurProducts />} />
          </Route>
        </Route>

        <Route element={<RequireAuth allow={["admin", "superadmin"]} />}>
          {/* Admin (company owner) dashboard */}
          <Route path="/admin-co" element={<DashboardLayout role="admin" />}>
            <Route index element={<CompanyOverview />} />
            <Route path="team" element={<CompanyUsers />} />
            {/* The three role pages folded into Team & Tokens; role is a filter
                chip there. Redirects so existing links don't dead-end. */}
            <Route path="users" element={<Navigate to="/admin-co/team" replace />} />
            <Route path="project-managers" element={<Navigate to="/admin-co/team" replace />} />
            <Route path="developers" element={<Navigate to="/admin-co/team" replace />} />
            {/* The SAME screens the PM uses. An admin isn't a lesser PM — they
                can already do everything a PM can on the server — so a second,
                thinner projects page just meant the two drifted apart. */}
            <Route path="projects" element={<PmProjects />} />
            <Route path="projects/:id" element={<PmProjectDetail />} />
            <Route path="projects/:id/:kind/:batchId" element={<PmBatchReport />} />
            <Route path="activity" element={<RunsBugs />} />
            <Route path="analytics" element={<CompanyAnalytics />} />
            {/* WITHDRAWN — Integrations and API Keys are no longer offered on the
                company surface. The routes are commented out rather than deleted so
                nothing is reachable by URL either: a nav entry alone is only a
                hidden page, not a withdrawn feature. Every file behind them
                (pages/company/Integrations.jsx, pages/company/ApiKeys.jsx,
                api/integrations.js and the backend routers) is untouched — restore
                by uncommenting these two routes, their imports above, and their
                entries in config/nav.js. */}
            {/* <Route path="integrations" element={<CompanyIntegrations />} /> */}
            <Route path="usage-report" element={<CompanyUsageReport />} />
            <Route path="referral" element={<Referral />} />
            <Route path="whats-new" element={<WhatsNew />} />
            <Route path="billing" element={<CompanyBilling />} />
            <Route path="activity-log" element={<CompanyActivityLog />} />
            {/* <Route path="keys" element={<CompanyApiKeys />} /> */}
            <Route path="settings" element={<CompanySettings />} />
            <Route path="notifications" element={<Notifications />} />
            {/* Inside this shell, so it keeps the company-admin sidebar + topbar. */}
            <Route path="our-other-products" element={<OurProducts />} />
          </Route>
        </Route>

        <Route element={<RequireAuth allow={["superadmin"]} />}>
          {/* SuperAdmin dashboard */}
          <Route path="/admin" element={<DashboardLayout role="superadmin" />}>
            <Route index element={<AdminOverview />} />
            <Route path="companies" element={<Companies />} />
            <Route path="companies/:id" element={<CompanyDetail />} />
            <Route path="analytics" element={<Analytics />} />
            <Route path="usage-report" element={<PlatformUsage />} />
            <Route path="models" element={<ModelConfig />} />
            <Route path="keys" element={<ApiKeys />} />
            <Route path="guardrails" element={<Guardrails />} />
            <Route path="firewall" element={<Firewall />} />
            <Route path="subscriptions" element={<Subscriptions />} />
            <Route path="billing" element={<Billing />} />
            <Route path="plans" element={<Plans />} />
            <Route path="coupons" element={<Coupons />} />
            <Route path="qa-context" element={<QaContext />} />
            <Route path="country-pricing" element={<CountryPricing />} />
            <Route path="changelog" element={<Changelog />} />
            <Route path="platform-ops" element={<PlatformOps />} />
            {/* No "whats-new" route under /admin, on purpose. The SuperAdmin
                AUTHORS the changelog at /admin/changelog; the consumer feed is
                for the roles an announcement is addressed to. Dropping the route
                as well as the sidebar entry is what makes this an access
                decision rather than a hidden link: /admin/whats-new now matches
                nothing and falls through to NotFound. The WhatsNew component
                itself is untouched and still mounted for tester, developer, PM
                and company admin below. */}
            <Route path="system" element={<System />} />
            <Route path="support" element={<Support />} />
            <Route path="audit" element={<AuditLogPage />} />
            <Route path="settings" element={<AdminSettings />} />
            <Route path="notifications" element={<Notifications />} />
            {/* Inside this shell, so it keeps the superadmin sidebar + topbar. */}
            <Route path="our-other-products" element={<OurProducts />} />
          </Route>
        </Route>

        <Route path="*" element={<NotFound />} />
      </Routes>
    </>
  );
}
